import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Layers, Network, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { useDailyPlan } from '@/hooks/mindspace/useRevision';

/** The soft brand tint the app's icon chips use - the theme's, not a hue per
 *  space. */
const CHIP = 'dark:tint-brand bg-brand-100 text-brand-700';

/**
 * The three Mindspace pages as a bento: the two daily ones side by side, the
 * archive across the bottom with the dot motif its graph is drawn in. Each
 * tile is one link named by the space. A tile shows today's number where the
 * daily plan has one ("7 due", "2 today") - the same cached query the Today
 * card reads, so no extra request.
 */
export function MindspaceSpaces() {
  const { data: plan } = useDailyPlan();
  const due = plan?.summary.practice.count ?? 0;
  const bytes = plan?.summary.quick_bytes.count ?? 0;

  return (
    <nav aria-labelledby='mindspace-spaces'>
      <h2
        id='mindspace-spaces'
        className='mb-3 px-1 font-mono text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground'
      >
        Your spaces
      </h2>
      <ul className='grid grid-cols-2 gap-3'>
        <li>
          <SpaceTile
            to='/mindspace/practice-arena'
            name='Practice Arena'
            description='Flashcards and quizzes'
            icon={Layers}
            stat={due > 0 ? `${due} due` : undefined}
            delay={80}
          />
        </li>
        <li>
          <SpaceTile
            to='/mindspace/quick-bytes'
            name='Quick Bytes'
            description='Bite-sized cards'
            icon={Zap}
            stat={bytes > 0 ? `${bytes} today` : undefined}
            delay={140}
          />
        </li>
        <li className='col-span-2'>
          <SpaceTile
            to='/mindspace/knowledge-archive'
            name='Knowledge Archive'
            description='Every concept, mapped'
            icon={Network}
            wide
            delay={200}
          >
            <DotMotif />
          </SpaceTile>
        </li>
      </ul>
    </nav>
  );
}

function SpaceTile({
  to,
  name,
  description,
  icon: Icon,
  stat,
  wide = false,
  delay,
  children,
}: {
  to: string;
  name: string;
  description: string;
  icon: LucideIcon;
  stat?: string;
  wide?: boolean;
  /** Stagger, so the tiles land one after another rather than as a slab. */
  delay: number;
  children?: ReactNode;
}) {
  return (
    <Link
      to={to}
      style={{ animationDelay: `${delay}ms` }}
      className={cn(
        // `relative isolate`: over the page lattice (a positioned layer).
        'motion-safe:animate-fade-in-up group relative isolate flex h-full overflow-hidden rounded-3xl bg-card p-4 ring-1 ring-inset transition-transform duration-fast active:scale-[0.98] motion-safe:fill-mode-both motion-reduce:active:scale-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        wide ? 'min-h-[7.5rem] items-center gap-4' : 'min-h-[10rem] flex-col'
      )}
    >
      <span
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
          CHIP
        )}
      >
        <Icon className='h-5 w-5' aria-hidden='true' />
      </span>
      <span className={cn('min-w-0', wide ? 'flex-1' : 'mt-auto pt-4')}>
        {stat && (
          <span
            className={cn(
              'mb-1 block font-mono text-xs font-semibold uppercase tracking-wide text-muted-foreground'
            )}
          >
            {stat}
          </span>
        )}
        <span className='block text-base font-semibold leading-tight text-foreground'>
          {name}
        </span>
        <span className='mt-0.5 block text-sm leading-snug text-muted-foreground'>
          {description}
        </span>
      </span>
      {children}
      <ArrowUpRight
        className='absolute right-3 top-3 h-4 w-4 text-muted-foreground transition-transform duration-fast group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transition-none'
        aria-hidden='true'
      />
    </Link>
  );
}

/** Lit dots - indices into a 5x4 grid - like a corner of the concept graph. */
const LIT = new Set([1, 7, 9, 13, 18]);

/**
 * A corner of the archive's graph: a small dot grid with a few concepts lit,
 * which glow in turn. Decoration - hidden from screen readers, still under
 * reduced motion.
 */
function DotMotif() {
  return (
    <span aria-hidden='true' className='grid shrink-0 grid-cols-5 gap-1.5 pr-5'>
      {Array.from({ length: 20 }, (_, i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            LIT.has(i)
              ? 'bg-primary motion-safe:animate-pulse'
              : 'bg-muted-foreground/25'
          )}
          style={LIT.has(i) ? { animationDelay: `${i * 120}ms` } : undefined}
        />
      ))}
    </span>
  );
}
